import { useMemo } from 'react';

import { useBillModuleProtectionData } from '@/components/bill/BillModuleProtection';
import { ECheckoutSteps, EStatus, ICheckoutSteps } from '@/services/types/core/checkoutSteps';

interface UseModifiedCheckoutStepsParams {
  checkoutSteps: ICheckoutSteps | null;
  completedVirtualSteps: Set<ECheckoutSteps>;
}

// Calculate modified checkout steps with injected Trip Insurance step for backwards compatibility
export const useModifiedCheckoutSteps = ({
  checkoutSteps,
  completedVirtualSteps,
}: UseModifiedCheckoutStepsParams): ICheckoutSteps | null => {
  const { availableTripInsuranceService } = useBillModuleProtectionData();

  // Calculate modified checkout steps with injected Trip Insurance step for backwards compatibility
  return useMemo(() => {
    if (!checkoutSteps) return checkoutSteps;

    let modified = checkoutSteps;
    if (checkoutSteps.steps && availableTripInsuranceService) {
      const existingTripInsuranceStep = checkoutSteps.steps.find(
        step => step.name === ECheckoutSteps.TRIP_INSURANCE,
      );

      // Only inject the step if it doesn't exist in backend response (backwards compatibility)
      if (!existingTripInsuranceStep) {
        // Find the payment step index to insert before it
        const paymentStepIndex = checkoutSteps.steps.findIndex(
          step => step.name === ECheckoutSteps.PAYMENT,
        );

        if (paymentStepIndex > -1) {
          const newSteps = [...checkoutSteps.steps];

          // Determine status based on whether the virtual step was completed
          const tripInsuranceStepStatus = completedVirtualSteps.has(ECheckoutSteps.TRIP_INSURANCE)
            ? EStatus.COMPLETE
            : EStatus.INCOMPLETE;

          const newTripInsuranceStep = {
            name: ECheckoutSteps.TRIP_INSURANCE,
            order: paymentStepIndex + 1,
            status: tripInsuranceStepStatus,
          };

          // Insert the trip insurance step before payment
          newSteps.splice(paymentStepIndex, 0, newTripInsuranceStep);

          // Update order numbers for subsequent steps
          newSteps.forEach((step, index) => {
            step.order = index + 1;
          });

          modified = {
            ...checkoutSteps,
            steps: newSteps,
          };
        }
      }
    }
    return modified;
  }, [checkoutSteps, completedVirtualSteps, availableTripInsuranceService]);
};
